import React, { useEffect, useRef } from "react";
import { pt } from "./strings";

// Shared patient-page chrome in the BrainyCare forms look (forms.css .bcf-*):
// the top bar (brand mark, practice name as text, EN/ES toggle) and the
// full-page status screens (invalid / expired / locked / ...).

export function LangToggle({ lang, onChange }) {
  return (
    <div className="bcf-lang" role="group" aria-label={pt("language", lang)}>
      <button type="button" lang="en" aria-pressed={lang === "en"} onClick={() => onChange("en")}>EN</button>
      <button type="button" lang="es" aria-pressed={lang === "es"} onClick={() => onChange("es")}>ES</button>
    </div>
  );
}

export function PatientTop({ practiceName, title, lang, onLangChange, children }) {
  return (
    <header className="bcf-top">
      <div className="bcf-top-inner">
        <div className="bcf-top-row">
          <div className="bcf-brand">
            <span className="bcf-brand-mark" aria-hidden="true">B</span>
            <div style={{ minWidth: 0 }}>
              <div className="bcf-form-title">{title || pt("docTitle", lang)}</div>
              {practiceName && <div className="bcf-practice">{practiceName}</div>}
            </div>
          </div>
          {onLangChange && <LangToggle lang={lang} onChange={onLangChange} />}
        </div>
        {children}
      </div>
    </header>
  );
}

// Moves focus to the screen's heading when it appears, so screen readers
// announce the new screen (and keyboard users start at the top).
export function useFocusOnMount(deps = []) {
  const ref = useRef(null);
  useEffect(() => {
    const h = setTimeout(() => {
      if (ref.current && ref.current.focus) {
        try { ref.current.focus({ preventScroll: true }); } catch (_) { ref.current.focus(); }
      }
    }, 0);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

const ICONS = {
  ok: <path d="M20 6 9 17l-5-5" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  lock: (<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>),
  link: (<><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" /><path d="M4 4l16 16" /></>),
  wifi: (<><path d="M2 8.8a15 15 0 0 1 20 0" /><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8.5 16a5 5 0 0 1 7 0" /><circle cx="12" cy="19.5" r="0.8" /></>),
  wait: (<><path d="M7 3h10M7 21h10" /><path d="M8 3c0 5 8 5 8 9s-8 4-8 9" /><path d="M16 3c0 5-8 5-8 9s8 4 8 9" /></>),
  device: (<><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M11 18.5h2" /></>),
};

export function StatusIcon({ icon = "ok", tone = "ok" }) {
  return (
    <div className={`bcf-status-icon tone-${tone}`} aria-hidden="true">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {ICONS[icon] || ICONS.ok}
      </svg>
    </div>
  );
}

export function PhoneLink({ phone }) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return <a className="bcf-status-phone" href={`tel:${digits}`}>{phone}</a>;
}

// "Please contact {practice} at {phone} for a new link." with the phone as a
// tap-to-call link.
export function ContactLine({ practice, lang }) {
  const name = practice && practice.name;
  const phone = practice && practice.phone;
  if (name && phone) {
    const [before, after = ""] = pt("contactPhone", lang, { practice: name }).split("{phone}");
    return (<>{before}<PhoneLink phone={phone} />{after}</>);
  }
  if (name) return <>{pt("contactNoPhone", lang, { practice: name })}</>;
  return <>{pt("contactGeneric", lang)}</>;
}

// kind: invalid | expired | cancelled | locked | completed | rate | network | kioskEnded
const KIND = {
  invalid: { icon: "link", tone: "muted", title: "invalidTitle", body: "invalidBody", contact: true },
  expired: { icon: "clock", tone: "warn", title: "expiredTitle", contact: true },
  cancelled: { icon: "link", tone: "muted", title: "cancelledTitle", contact: true },
  locked: { icon: "lock", tone: "danger", title: "lockedTitle", body: "lockedBody", contact: true },
  completed: { icon: "ok", tone: "ok", title: "completedTitle", body: "completedBody" },
  rate: { icon: "wait", tone: "warn", title: "rateTitle", body: "rateBody" },
  network: { icon: "wifi", tone: "muted", title: "networkTitle", body: "networkBody" },
  kioskEnded: { icon: "device", tone: "muted", title: "kioskEnded", body: "doneKiosk" },
};

// bilingual: also print the title/body in the other language (screens shown
// before the patient's language is known, e.g. a broken link).
export function StatusScreen({ kind, practice, lang, onLangChange, onRetry, children, hideContact, bilingual }) {
  const k = KIND[kind] || KIND.invalid;
  const headRef = useFocusOnMount([kind]);
  const other = lang === "es" ? "en" : "es";
  return (
    <div className="bcf bcf-patient" lang={lang} data-screen={kind}>
      <PatientTop practiceName={practice && practice.name} lang={lang} onLangChange={onLangChange} />
      <main className="bcf-shell bcf-status-shell">
        <div className="bcf-status-card" role={kind === "completed" ? "status" : undefined}>
          <StatusIcon icon={k.icon} tone={k.tone} />
          <h1 className="bcf-status-title" ref={headRef} tabIndex={-1}>{pt(k.title, lang)}</h1>
          {k.body && <p className="bcf-status-body">{pt(k.body, lang)}</p>}
          {k.contact && !hideContact && (
            <p className="bcf-status-body"><ContactLine practice={practice} lang={lang} /></p>
          )}
          {bilingual && (
            <div className="bcf-status-alt" lang={other}>
              <p className="bcf-status-alt-title">{pt(k.title, other)}</p>
              {k.body && <p>{pt(k.body, other)}</p>}
              {k.contact && !hideContact && <p><ContactLine practice={practice} lang={other} /></p>}
            </div>
          )}
          {children}
          {onRetry && (
            <div className="bcf-status-actions">
              <button type="button" className="bcf-btn bcf-btn-primary" onClick={onRetry}>{pt("retry", lang)}</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function Loading({ lang }) {
  return (
    <div className="bcf bcf-patient" lang={lang} data-screen="loading">
      <main className="bcf-shell bcf-status-shell">
        <div className="bcf-loading" role="status" aria-live="polite">
          <span className="bcf-spinner" aria-hidden="true" />
          <span>{pt("loading", lang)}</span>
        </div>
      </main>
    </div>
  );
}
